/* @module csi/widget/IFrameDialog */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/on",
	"csi/util",
	"dijit/form/Button",
	"dijit/registry",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/widget/Dialog"
], function(declare, lang, dom, domClass, domConstruct, on, util, Button, registry, _, html, locale, Dialog) {

	const _BUTTON_PRIMARY = "__primary";
	const _BUTTON_CANCEL = "C";

	const IFrameDialog = declare(Dialog, {

		/** @property {blob} iframeSrc - The iframe source represented by the response data */
		iframeSrc: null,

		/** @property {boolean} iframeId - The UUID of the iframe element */
		iframeId: null,

		/** @property {URL} blobUrl - The URL which is generated from the response data */
		blobUrl: null,

		/** @property {PostRequest} iframeRequest - The PostRequest which fetches data from the external API */
		iframeRequest: null,

		/** @property {boolean} destroyOnHide - Destroys the window when it's hidden */
		destroyOnHide: true,

		/** @property {boolean} bShowException - Show exception message immediately */
		bShowException: false,

		/** @property {String[]} buttons - Array of strings identifying available buttons */
		buttons: null,

		/** @property {exception} exception - Server exception to display along with error */
		exception: null,

		/** @property {string} fontAwesomeIcon - Font awesome icon name, if used */
		fontAwesomeIcon: null,

		/** @property {string} iconClass - CSS class to display icon */
		iconClass: null,

		/** @property {boolean} materialIcon - Material icon name, if used material icons */
		materialIcon: null,

		/** @property {string} message - Message to display to user */
		message: null,

		/** @property {string} mimeType - The mimeType of the expected response */
		mimeType: null,

		/** @property {string} frameStyle -  The iframe style CSS value */
		frameStyle: null,

		onCreate: function() {
			// summary: populate dialog with iframe source data, icon, message, and buttons
			const blob = new Blob([this.iframeSrc], {type: this.mimeType});
			this.blobUrl = window.URL.createObjectURL(blob);

			const iframe = domConstruct.create("iframe", {
				"style": this.frameStyle,
				src: this.blobUrl
			}, this.containerNode);
			this.iframeId = util.uuid();
			iframe.id = this.iframeId;

			domClass.add(this.domNode, "csi-iframe-dialog");

			// add icon
			if (this.iconClass) {
				domConstruct.create("div", {
					"class": "csiActionIcon " + this.iconClass,
					"style": "vertical-align: middle"
				}, iframe, "before");
			}

			// add material icon
			if (this.materialIcon) {
				domConstruct.create("div", {
					"class": "csi-message-icon material-icons csi-material-icon-" + this.materialIcon,
					innerHTML: this.materialIcon
				}, iframe, "before");
			}

			// add font awesome
			if (this.fontAwesomeIcon) {
				domConstruct.create("div", {
					"class": "csi-message-icon " + this.fontAwesomeIcon
				}, iframe, "before");
			}

			if (this.onMessage) {
				this.onMessage(iframe);
			}

			// add exception UI
			if (this.exception) {
				if (this.bShowException) {
					this._showException(iframe, "after");
				} else {
					const divException = domConstruct.create("div", {"class": "csi-message-exception-link"}, iframe, "after");
					const a = html.a({
						"class": html.CSS_BROWSE,
						innerText: locale.resource("ShowException")
					}, divException);

					this.own(on(a, "click", lang.hitch(this, this._onException)));
				}
			}

			// add buttons
			if (this.buttons) {
				const divActionsNode = domConstruct.create("div", {"class": "csiActionsNode"}, this.containerNode);
				const divButtons = domConstruct.create("div", {"class": "csiActions"}, divActionsNode);
				let btnTabIndex = 2;
				const forcePrimary = this.buttons && (this.buttons.length === 1)
					&& this.buttons[0] !== IFrameDialog.BUTTON_OK && this.buttons[0] !== IFrameDialog.BUTTON_CLOSE;

				_.each(this.buttons, function(key) {
					let currBtnTabIndex = btnTabIndex++;
					let btnClass = 'csi-secondary-btn';
					if (key === _BUTTON_CANCEL) {
						btnClass = 'csi-link-btn';
					}
					if (forcePrimary || key.indexOf(_BUTTON_PRIMARY) >= 0) {
						btnClass = 'csi-primary-btn';
						key = key.replace(_BUTTON_PRIMARY, '');
						currBtnTabIndex = 1;
					}

					const button = new Button({
						label: _.escape(locale.resource("BUTTON_" + key)),
						"class": key === IFrameDialog.BUTTON_REFRESH ? 'csi-primary-btn' : btnClass,
						buttonKey: key
					}, domConstruct.create("button", null, divButtons));
					if (key === IFrameDialog.BUTTON_REFRESH && this.iframeRequest) {
						this.connect(button, "onClick", "onRefresh");
					} else {
						this.connect(button, "onClick", "onButton");
					}
					button.startup();
					button.focusNode.tabIndex = currBtnTabIndex;
				}, this);
			}
		},

		onButton: function(/*Event*/e) {
			// summary: respond to button click
			if (this.onResponse) {
				this.onResponse(registry.getEnclosingWidget(e.currentTarget).buttonKey, this.get("value"), e);
			}

			URL.revokeObjectURL(this.blobUrl);
			this.hide();
		},

		onRefresh: function(/*Event*/e) {
			// summary: respond to REFRESH event by fetching iframe's content via executing its PostRequest
			if (registry.getEnclosingWidget(e.currentTarget).buttonKey === IFrameDialog.BUTTON_REFRESH) {
				this.iframeRequest.post({}).then(lang.hitch(this, this._handleOnRefresh));
			}
		},

		onCancel: function() {
			// summary: respond to dialog being closed
			if (this.onResponse) {
				this.onResponse("C", this);
			}
		},

		_onException: function(/*Event*/e) {
			// summary: toggle display of exception
			this._showException(e.target.parentNode, "after");
			domConstruct.destroy(e.target.parentNode);

			// resize twice because Dojo positions the dialog before resizing it, so in some cases the first time does not fully work
			this.resize();
			this.resize();
		},

		_showException: function(/*DOMNode*/domNodeBefore, /*String?*/pos) {
			// summary: display exception stack trace
			html.create("pre", {innerText: this.exception}, html.create("div", {"class": "csi-message-exception"}, domNodeBefore, pos));
		},

		_handleOnRefresh: function(response) {
			if (response && response.data) {
				const blob = new Blob([response.data], {type: this.mimeType});
				URL.revokeObjectURL(this.blobUrl);
				this.blobUrl = window.URL.createObjectURL(blob);
				const iframe = dom.byId(this.iframeId);
				iframe.src = this.blobUrl;
			}
		},

		/**
		 * Return the action buttons on a dialog.
		 * @return {Button[]} - array of buttons
		 */
		getAllButtons: function() {
			return _.filter(registry.findWidgets(this.domNode), function(widget) {
				return widget.type === "button";
			});
		},

		/**
		 * If button key is present and valid, returns only a button that match the key.
		 * @param {string} key - button key
		 * @return {Button} - button
		 */
		getActionButton: function(key) {
			return _.findWhere(this.getAllButtons(), {buttonKey: key});
		}
	});

	// common constants
	lang.mixin(IFrameDialog, {
		// Button type
		BUTTON_OK: "OK",
		BUTTON_CLOSE: "CLOSE",
		BUTTON_UPDATE: "UPDATE",
		BUTTON_REFRESH: "REFRESH",

		// Button class.
		BUTTON_PRIMARY: _BUTTON_PRIMARY,

		// Defined Material Icons:
		ICON_ERROR: 'error',
		ICON_WARNING: 'warning',
		ICON_COMPLETED: 'check_circle',
		ICON_QUESTION: 'help',
		ICON_INFO: 'info'
	});

	return IFrameDialog;
});